import * as React from 'react'
import * as formik from 'formik'

import { safePusher } from './safePush'
import { CATEGORY, EVENT_TYPE, type GenericEvent } from './types'

export type TrackedFields = {
  [field: string]: {
    value: string
    error: string
  }
}

type Setter = {
  setValue: (value: string) => void
  markFilled: () => void
  markEmpty: () => void
}

type FieldSetters<TRACKED_FIELDS extends TrackedFields> = {
  [K in keyof TRACKED_FIELDS]: Setter
}

type FormEvent = Pick<GenericEvent, 'eventLabel' | 'eventCallback' | 'eventAction'>

export type FormTracker<TRACKED_FIELDS extends TrackedFields> = FieldSetters<TRACKED_FIELDS> & {
  pushErrorEvent(event: string, error: formik.FormikErrors<any>): void
  pushFormEvent(formEvent: FormEvent): void
}

export function useFormTracking<TRACKED_FIELDS extends TrackedFields>(
  eventCategory: CATEGORY,
  trackedFields: TRACKED_FIELDS,
  gtmContainerId?: string | undefined,
): FormTracker<TRACKED_FIELDS> {
  // We don't keep the state in a state, because we don't need a rerender on
  // state update. We juste need a persistent object we can update.
  const trackingState = React.useRef<Record<string, string>>({})

  const safePush = React.useMemo(() => safePusher(gtmContainerId), [])

  // On mount, send the list of value/error for tracked field. On mount the form
  // is necessarly empty and without error
  React.useEffect(() => {
    const [stateVariables, errorVariables] = Object.entries(trackedFields).reduce<
      [Record<string, string>, Record<string, string>]
    >(
      (accumulator, [_, { value, error }]) => {
        const [stateVariables, errorVariables] = accumulator
        stateVariables[value] = 'empty'
        errorVariables[error] = 'none'
        return accumulator
      },
      [{}, {}],
    )

    // Naked push on datalayer. Not risky since we don't need to use any
    // callback. If ga is blocked and the push is simply a noop.
    window.dataLayer?.push(stateVariables)
    window.dataLayer?.push(errorVariables)
  }, [])

  const setters = (Object.keys(trackedFields) as (keyof TRACKED_FIELDS & string)[]).reduce<
    FieldSetters<TRACKED_FIELDS>
  >((accumulator, key) => {
    accumulator[key] = {
      setValue: (value: string) => setState(key, value),
      markFilled: () => setState(key, 'filled'),
      markEmpty: () => setState(key, 'empty'),
    }

    return accumulator
  }, {} as any)

  return { ...setters, pushErrorEvent, pushFormEvent }

  function pushErrorEvent(event: string, errors: formik.FormikErrors<any>): void {
    const messages: Record<string, string> = {}
    for (const [field, message] of Object.entries(errors)) {
      if (message && trackedFields[field]) {
        messages[trackedFields[field].error] = String(message)
      }
    }
    if (Object.keys(messages).length !== 0) {
      window.dataLayer?.push({ event, ...messages })
    }
  }

  function pushFormEvent(formEvent: FormEvent): void {
    const event = {
      event: EVENT_TYPE.GENERIC,
      eventCategory,
      ...formEvent,
    } as GenericEvent

    if (event.eventCallback && !event.eventTimeout) {
      event.eventTimeout = 500
    }

    safePush(event)
  }

  function setState(field: string, value: string): void {
    // noop value has not changed
    if (trackingState.current[field] === value) {
      return
    }

    trackingState.current[field] = value
    window.dataLayer?.push({
      event: EVENT_TYPE.GENERIC,
      eventCategory,
      [trackedFields[field].value]: value,
    })
  }
}
